import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import paymentApi, {
  CreatePaymentRequest,
  SaveCardRequest,
  ResumePlanRequest,
  ChargeRequest,
  RefundRequest,
  CreatePlanRequest,
  UpdateOrganizationPlanRequest,
  SubscriptionCheckResponse
} from '../api/payment.api'
import { QueryKeys } from './querykeys'

export const useQueryGetCards = () => {
  return useQuery({
    queryKey: [QueryKeys.GET_PAYMENT_CARDS],
    queryFn: async () => {
      try {
        const response = await paymentApi.getCards()
        if (response.status === 200) {
          const data = response.data as { cards?: unknown[]; customerId?: string }
          return {
            cards: data?.cards ?? [],
            customerId: data?.customerId ?? null
          }
        }
      } catch (error) {
        return {
          cards: [],
          customerId: null
        }
      }
      return {
        cards: [],
        customerId: null
      }
    }
  })
}

export const useMutationCreatePayment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (paymentData: CreatePaymentRequest) => {
      const response = await paymentApi.createPayment(paymentData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
    }
  })
}

export const useMutationSaveCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (cardData: SaveCardRequest) => {
      const response = await paymentApi.saveCard(cardData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
    }
  })
}

export const useMutationChargeCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ cardId, chargeData }: { cardId: number; chargeData: ChargeRequest }) => {
      const response = await paymentApi.chargeCard(cardId, chargeData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
    }
  })
}

export const useMutationRefundPayment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      cardId,
      paymentId,
      refundData
    }: {
      cardId: number
      paymentId: number
      refundData?: RefundRequest
    }) => {
      const response = await paymentApi.refundPayment(cardId, paymentId, refundData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
    }
  })
}

export const useMutationUpdateCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ cardId, name }: { cardId: number; name: string }) => {
      const response = await paymentApi.updateCard(cardId, name)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
    }
  })
}

export const useMutationCheckCardCanDelete = () => {
  return useMutation({
    mutationFn: async (cardId: number) => {
      return paymentApi.canDeleteCard(cardId)
    }
  })
}

export const useMutationDeleteCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (cardId: number) => {
      return paymentApi.deleteCard(cardId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
    }
  })
}

export const useMutationUpdateSubscriptionCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      organizationId,
      subscriptionId,
      body
    }: {
      organizationId: number
      subscriptionId: number
      body: SaveCardRequest
    }) => {
      const response = await paymentApi.updateSubscriptionCard(organizationId, subscriptionId, body)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS] })
    }
  })
}

export const useMutationUpdateAddonSubscriptionCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      organizationId,
      addonSubscriptionId,
      body
    }: {
      organizationId: number
      addonSubscriptionId: number
      body: SaveCardRequest
    }) => {
      const response = await paymentApi.updateAddonSubscriptionCard(organizationId, addonSubscriptionId, body)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ADDON_SUBSCRIPTIONS] })
    }
  })
}

export const useQueryGetPlans = () => {
  return useQuery({
    queryKey: [QueryKeys.GET_PAYMENT_PLANS],
    queryFn: async () => {
      try {
        const response = await paymentApi.getPlans()
        if (response.status === 200) {
          return response.data
        }
      } catch (error) {
        return { plans: [], ufValue: 0 }
      }
      return { plans: [], ufValue: 0 }
    }
  })
}

export const useMutationCreatePlan = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (planData: CreatePlanRequest) => {
      const response = await paymentApi.createPlan(planData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_PLANS] })
    }
  })
}

export const useMutationUpdateOrganizationPlan = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (planId: number) => {
      const response = await paymentApi.updateOrganizationPlan(planId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
    }
  })
}

export const useMutationUpdateOrganizationPlanWithCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, planData }: { organizationId: number; planData: UpdateOrganizationPlanRequest }) => {
      const response = await paymentApi.updateOrganizationPlanWithCard(organizationId, planData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS] })
    }
  })
}

export const useMutationCreatePlanSubscription = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, planData }: { organizationId: number; planData: { planId: number; cardId: number; token: string; payerEmail?: string } }) => {
      const response = await paymentApi.createPlanSubscription(organizationId, planData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS] })
    }
  })
}

export const useMutationConfirmPlanSubscription = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, subscriptionId, token }: { organizationId: number; subscriptionId: number; token: string }) => {
      const response = await paymentApi.confirmPlanSubscription(organizationId, subscriptionId, { token })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_TOKENS_AVAILABLE] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENTS] })
    }
  })
}

export const useQueryGetOrganizationSubscriptions = (organizationId: number | null) => {
  return useQuery({
    queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS, organizationId],
    queryFn: async () => {
      if (!organizationId) return { plans: [], subscriptions: [], addonSubscriptions: [], currentPlan: null, ufValue: 0 }
      try {
        const response = await paymentApi.getOrganizationSubscriptions(organizationId)
        if (response.status === 200) {
          const data = response.data as typeof response.data & { addonSubscriptions?: unknown[] }
          return { ...data, addonSubscriptions: data.addonSubscriptions ?? [] }
        }
      } catch (error) {
        return { plans: [], subscriptions: [], addonSubscriptions: [], currentPlan: null, ufValue: 0 }
      }
      return { plans: [], subscriptions: [], addonSubscriptions: [], currentPlan: null, ufValue: 0 }
    },
    enabled: !!organizationId
  })
}

export const useQueryGetPayments = () => {
  return useQuery({
    queryKey: [QueryKeys.GET_PAYMENTS],
    queryFn: async () => {
      try {
        const response = await paymentApi.getPayments()
        if (response.status === 200) {
          return response.data
        }
      } catch (error) {
        return []
      }
      return []
    }
  })
}

export const useQueryGetAddons = () => {
  return useQuery({
    queryKey: [QueryKeys.GET_ADDONS],
    queryFn: async () => {
      try {
        const response = await paymentApi.getAddons()
        if (response.status === 200) {
          return response.data
        }
      } catch (error) {
        return { addons: [], ufValue: 0 }
      }
      return { addons: [], ufValue: 0 }
    }
  })
}

export const useQueryGetAddonSubscriptions = (organizationId: number | null) => {
  return useQuery({
    queryKey: [QueryKeys.GET_ADDON_SUBSCRIPTIONS, organizationId],
    queryFn: async () => {
      if (!organizationId) return []
      try {
        const response = await paymentApi.getAddonSubscriptions(organizationId)
        if (response.status === 200) {
          return response.data
        }
      } catch (error) {
        return []
      }
      return []
    },
    enabled: !!organizationId
  })
}

export const useQueryGetOrganizationSubscriptionStatus = (organizationId: number | null) => {
  return useQuery({
    queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTION_STATUS, organizationId],
    queryFn: async () => {
      if (!organizationId) {
        return null as SubscriptionCheckResponse | null
      }
      try {
        const response = await paymentApi.checkOrganizationSubscription(organizationId)
        if (response.status === 200) {
          return response.data
        }
      } catch (error) {
        return null as SubscriptionCheckResponse | null
      }
      return null as SubscriptionCheckResponse | null
    },
    enabled: !!organizationId
  })
}

export const useMutationCreateAddonSubscription = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      organizationId,
      addons,
      cardId,
      token,
      payerEmail
    }: {
      organizationId: number
      addons: { addonId: number; quantity: number }[]
      cardId: number
      token: string
      payerEmail?: string
    }) => {
      const body: { addons: { addonId: number; quantity: number }[]; cardId: number; token: string; payerEmail?: string } = {
        addons,
        cardId,
        token
      }
      if (payerEmail != null && payerEmail !== '') {
        body.payerEmail = payerEmail
      }
      const response = await paymentApi.createAddonSubscription(organizationId, body)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_ADDON_SUBSCRIPTIONS, variables.organizationId]
      })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS] })
    }
  })
}

export const useMutationConfirmAddonSubscription = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      organizationId,
      addonSubscriptionId,
      token
    }: {
      organizationId: number
      addonSubscriptionId: number
      token: string
    }) => {
      const response = await paymentApi.confirmAddonSubscription(organizationId, addonSubscriptionId, { token })
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_ADDON_SUBSCRIPTIONS, variables.organizationId]
      })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_TOKENS_AVAILABLE] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS] })
    }
  })
}

export const useMutationCancelOrganizationPlan = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (organizationId: number) => {
      await paymentApi.cancelOrganizationPlan(organizationId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS] })
    }
  })
}

export const useMutationResumeOrganizationPlan = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, body }: { organizationId: number; body: ResumePlanRequest }) => {
      const response = await paymentApi.resumeOrganizationPlan(organizationId, body)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_TOKENS_AVAILABLE] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_CARDS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENTS] })
    }
  })
}
